import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/auth.store';
import { useWebSocket } from './useWebSocket';
import { getFirstAccessibleRoute } from '../utils/route-utils';
import type { PermissionChangeEvent } from '../services/websocket/websocket.service';

export const usePermissions = () => {
  const user = useAuthStore((state) => state.user);
  const loadUser = useAuthStore((state) => state.loadUser);
  const { addEventListener, removeEventListener, isConnected } = useWebSocket();
  const [permissionChangeNotification, setPermissionChangeNotification] = useState<PermissionChangeEvent | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  


  // Verificar si el usuario tiene la estructura completa con roles
  const getCompleteUser = () => {
    
    if (!user) {
      return null;
    }
    
    // Si ya tiene la estructura completa (roles con módulos), usarla directamente
    if ('roles' in user && Array.isArray(user.roles)) {
      return user as any;
    }
    
    // Si tiene la estructura básica, retornar null (no hay permisos detallados)
    return null;
  };

  const completeUser = getCompleteUser();

  // Verificar si el usuario tiene un permiso específico en un módulo
  const hasPermission = (moduleName: string, permissionName: string): boolean => {
    
    if (!completeUser?.roles) {
      return false;
    }

    const checkModulePermissions = (module: any, targetName: string): boolean => {
      if (module.nombre === targetName) {
        return module.permisos.some((permission: any) => permission.nombre === permissionName);
      }
      
      // Verificar en submódulos si existen
      if (module.submodulos && Array.isArray(module.submodulos)) {
        return module.submodulos.some((submodule: any) => checkModulePermissions(submodule, targetName));
      }
      
      return false;
    };

    // Para módulo Administradores, también verificar Usuarios (compatibilidad durante transición)
    // Para módulo Clientes, también verificar Membresías (compatibilidad durante transición)
    const modulesToCheck = moduleName === 'Administradores' 
      ? ['Administradores', 'Usuarios'] 
      : moduleName === 'Clientes'
      ? ['Clientes', 'Membresías']
      : [moduleName];

    const hasPermissionResult = completeUser.roles.some((role: any) => 
      role.modulos.some((module: any) => 
        modulesToCheck.some(name => checkModulePermissions(module, name))
      )
    );
    
    return hasPermissionResult;
  };

  // Verificar si el usuario tiene acceso a un módulo (cualquier permiso)
  const hasModuleAccess = (moduleName: string): boolean => {
    if (!completeUser?.roles) return false;

    const checkModuleAccess = (module: any, targetName: string): boolean => {
      if (module.nombre === targetName && module.permisos.length > 0) {
        return true;
      }
      
      // Verificar en submódulos si existen
      if (module.submodulos && Array.isArray(module.submodulos)) {
        return module.submodulos.some((submodule: any) => checkModuleAccess(submodule, targetName));
      }
      
      return false;
    };

    // Para módulo Administradores, también verificar Usuarios (compatibilidad durante transición)
    // Para módulo Clientes, también verificar Membresías (compatibilidad durante transición)
    const modulesToCheck = moduleName === 'Administradores' 
      ? ['Administradores', 'Usuarios'] 
      : moduleName === 'Clientes'
      ? ['Clientes', 'Membresías']
      : [moduleName];

    return completeUser.roles.some((role: any) => 
      role.modulos.some((module: any) => 
        modulesToCheck.some(name => checkModuleAccess(module, name))
      )
    );
  };

  // Obtener todos los permisos del usuario para un módulo específico
  const getModulePermissions = (moduleName: string): string[] => {
    if (!completeUser?.roles) return [];

    const permissions: string[] = [];
    
    const collectPermissions = (module: any, targetName: string) => {
      if (module.nombre === targetName) {
        module.permisos.forEach((permission: any) => {
          if (!permissions.includes(permission.nombre)) {
            permissions.push(permission.nombre);
          }
        });
      }
      
      // Verificar en submódulos si existen
      if (module.submodulos && Array.isArray(module.submodulos)) {
        module.submodulos.forEach((submodule: any) => collectPermissions(submodule, targetName));
      }
    };
    
    // Para módulo Administradores, también verificar Usuarios (compatibilidad durante transición)
    // Para módulo Clientes, también verificar Membresías (compatibilidad durante transición)
    const modulesToCheck = moduleName === 'Administradores' 
      ? ['Administradores', 'Usuarios'] 
      : moduleName === 'Clientes'
      ? ['Clientes', 'Membresías']
      : [moduleName];

    completeUser.roles.forEach((role: any) => {
      role.modulos.forEach((module: any) => {
        modulesToCheck.forEach(name => collectPermissions(module, name));
      });
    });

    return permissions;
  };

  // Verificar múltiples permisos (ANY - al menos uno)
  const hasAnyPermission = (moduleName: string, permissionNames: string[]): boolean => {
    return permissionNames.some(permission => hasPermission(moduleName, permission));
  };

  // Verificar múltiples permisos (ALL - todos)
  const hasAllPermissions = (moduleName: string, permissionNames: string[]): boolean => {
    return permissionNames.every(permission => hasPermission(moduleName, permission));
  };

  // Verificar si es administrador o super administrador (puede ver todo)
  const isAdmin = (): boolean => {
    if (!completeUser?.roles) return false;
    return completeUser.roles.some((rol: any) =>
      rol.nombre === 'Super Administrador' || rol.nombre === 'Administrador'
    );
  };

  // Verificar si es super administrador
  const isSuperAdmin = (): boolean => {
    if (!completeUser?.roles) return false;
    return completeUser.roles.some((rol: any) =>
      rol.nombre === 'Super Administrador'
    );
  };

  // Verificar si es vendedor
  const isVendedor = (): boolean => {
    if (!completeUser?.roles) return false;
    return completeUser.roles.some((rol: any) =>
      rol.nombre === 'Vendedor'
    );
  };

  // Mapeo de rutas a nombres de módulos
  const getModuleNameFromRoute = (pathname: string): string | null => {
    // Normalizar la ruta removiendo el prefijo /dashboard si existe
    const cleanPath = pathname.replace('/dashboard', '');
    
    if (cleanPath === '/' || cleanPath === '') return 'Dashboard';
    
    // Rutas específicas primero (más específicas antes que las generales)
    if (cleanPath.startsWith('/administradores')) return 'Administradores';
    if (cleanPath.startsWith('/clientes')) return 'Clientes';
    if (cleanPath.startsWith('/ventas')) return 'Ventas';
    if (cleanPath.startsWith('/seguridad/roles')) return 'Roles';
    if (cleanPath.startsWith('/seguridad/modulos')) return 'Módulos';
    if (cleanPath.startsWith('/seguridad/permisos')) return 'Permisos';
    if (cleanPath.startsWith('/seguridad/auditoria')) return 'Auditorías';
    if (cleanPath.startsWith('/pos')) return 'Punto de Venta';
    if (cleanPath.startsWith('/productos')) return 'Productos';
    if (cleanPath.startsWith('/proveedores')) return 'Proveedores';
    if (cleanPath.startsWith('/mi-farmacia/nomina')) return 'Nomina';
    if (cleanPath.startsWith('/mi-farmacia/productos-vendidos')) return 'Productos Vendidos';

    // Rutas generales después
    if (cleanPath.startsWith('/seguridad')) return 'Seguridad';
    if (cleanPath.startsWith('/configuracion')) return 'Configuración';
    if (cleanPath.startsWith('/mi-farmacia/reportes')) return 'Reportes';
    if (cleanPath.startsWith('/reportes')) return 'Reportes';
    if (cleanPath.startsWith('/cierre-caja')) return 'Cierre de Caja';

    return null;
  };
  
  // Verificar si el usuario puede acceder a la ruta actual
  const checkCurrentRouteAccess = useCallback(() => {
    const moduleName = getModuleNameFromRoute(location.pathname);
    if (moduleName) {
      const hasAccess = hasPermission(moduleName, 'ver');
      if (!hasAccess) {
        const fallbackRoute = completeUser ? getFirstAccessibleRoute(completeUser) : '/dashboard';
        navigate(fallbackRoute);
      }
    }
  }, [location.pathname, completeUser, navigate]);

  // Manejar cambios de permisos en tiempo real via WebSocket
  useEffect(() => {
    if (!isConnected || !user?.id) return;

    const handlePermissionChange = async (event: any) => {
      // Solo procesar eventos de cambio de permisos
      if (!event.action || !event.details) return;

      // Verificar si el evento afecta al usuario actual
      const isAffected = event.userId === user.id || 
                        (user as any)?.roles?.some((role: any) => role.id === event.roleId);

      if (isAffected) {
        setPermissionChangeNotification(event);
        
        try {
          await loadUser();
          
          // Log del usuario actual después de la recarga
          const currentUser = useAuthStore.getState().user;
          
          // Verificar si el usuario aún tiene acceso a la ruta actual
          setTimeout(() => {
            checkCurrentRouteAccess();
          }, 100); // Pequeño delay para asegurar que el estado se haya actualizado

        } catch (error) {
          console.error('❌ Error refreshing user permissions:', error);
        }
      }
    };

    // Agregar listener para cambios de permisos
    addEventListener('permission_change', handlePermissionChange);

    // Cleanup
    return () => {
      removeEventListener('permission_change', handlePermissionChange);
    };
  }, [isConnected, user?.id, loadUser, addEventListener, removeEventListener]);

  // Verificar acceso a la ruta actual cuando cambien los permisos o la ruta
  useEffect(() => {
    if (completeUser) {
      checkCurrentRouteAccess();
    }
  }, [completeUser, checkCurrentRouteAccess]);

  // Función para limpiar notificación de cambio
  const clearPermissionChangeNotification = () => {
    setPermissionChangeNotification(null);
  };

  return {
    hasPermission,
    hasModuleAccess,
    getModulePermissions,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isSuperAdmin,
    isVendedor,
    user: completeUser,
    // Nuevas propiedades relacionadas con WebSocket
    permissionChangeNotification,
    clearPermissionChangeNotification,
    isWebSocketConnected: isConnected
  };
};